<div class="certificate-page">
  <div class="page-header">
    <div class="header-icon">
      <i class="fas fa-certificate"></i>
    </div>
    <div>
      <h3>Certificate & Template Master</h3>
      <p>
        Design, render, and generate dynamic-SQL print templates using AI.
      </p>
    </div>
  </div>

  <!-- Navigation Tabs -->
  <div class="nav-tabs-container mb-3">
    <ul class="nav nav-pills custom-nav-pills">
      <li class="nav-item">
        <button
          type="button"
          class="nav-link"
          [class.active]="activeTab === 'render'"
          (click)="activeTab = 'render'"
        >
          <i class="fas fa-eye me-2"></i>
          Render Certificate
        </button>
      </li>
      <li class="nav-item">
        <button
          type="button"
          class="nav-link"
          [class.active]="activeTab === 'aiTemplate'"
          (click)="activeTab = 'aiTemplate'"
        >
          <i class="fas fa-magic me-2"></i>
          AI Dynamic SQL Template Generation
        </button>
      </li>
    </ul>
  </div>

  <!-- ============================================================== -->
  <!-- TAB 1: EXISTING CERTIFICATE RENDER WORKFLOW -->
  <!-- ============================================================== -->
  <div *ngIf="activeTab === 'render'">
    <div class="certificate-card selection-card">
      <div class="row g-3">
        <div class="col-lg-4 col-md-6">
          <label class="form-label">
            Select Module <span class="text-danger">*</span>
          </label>
          <select
            class="form-select"
            [(ngModel)]="selectedModuleName"
            (change)="onModuleChange()"
          >
            <option value="">Select Module</option>
            <option
              *ngFor="let module of moduleOptions"
              [value]="module.value"
            >
              {{ module.label }}
            </option>
          </select>
        </div>

        <div class="col-lg-4 col-md-6">
          <label class="form-label">
            Select {{ entityLabel }} <span class="text-danger">*</span>
          </label>
          <div
            *ngIf="isEntityLoading"
            class="form-control loading-control"
          >
            <span class="spinner-border spinner-border-sm me-2"></span>
            Loading {{ entityLabel }} list...
          </div>
          <select
            *ngIf="!isEntityLoading"
            class="form-select"
            [(ngModel)]="selectedEntityId"
            [disabled]="!selectedModuleName"
            (change)="onEntityChange()"
          >
            <option [ngValue]="0">Select {{ entityLabel }}</option>
            <option
              *ngFor="let entity of entityList"
              [ngValue]="getEntityId(entity)"
            >
              {{ getEntityName(entity) }}
            </option>
          </select>
          <small
            *ngIf="
              selectedModuleName &&
              !isEntityLoading &&
              entityList.length === 0
            "
            class="text-danger d-block mt-1"
          >
            No active/onboarded {{ entityLabel }} found.
          </small>
        </div>

        <div class="col-lg-4 col-md-6">
          <label class="form-label">
            Template Type <span class="text-danger">*</span>
          </label>
          <div
            *ngIf="isTemplateLoading"
            class="form-control loading-control"
          >
            <span class="spinner-border spinner-border-sm me-2"></span>
            Loading template types...
          </div>
          <select
            *ngIf="!isTemplateLoading"
            class="form-select"
            [(ngModel)]="selectedTemplateId"
            [disabled]="!selectedModuleName"
            (change)="onTemplateChange()"
          >
            <option value="">Select Template Type</option>
            <option
              *ngFor="let templateType of templateList"
              [value]="templateType"
            >
              {{ templateType }}
            </option>
          </select>
          <small
            *ngIf="
              selectedModuleName &&
              !isTemplateLoading &&
              templateList.length === 0
            "
            class="text-danger d-block mt-1"
          >
            No active base PrintMaster template found for {{ baseTemplateIdLabel }}.
          </small>
        </div>
      </div>
    </div>

    <div class="certificate-card editor-card">
      <div class="editor-block">
        <div class="editor-heading">
          <label>
            <i class="fas fa-heading me-2"></i>
            Header HTML
          </label>
          <span>@HeaderHtml</span>
        </div>
        <textarea
          class="form-control font-monospace"
          rows="7"
          [placeholder]="headerPlaceholder"
          [(ngModel)]="headerHtml"
          (input)="generatedCertificateHtml = ''"
        ></textarea>
      </div>

      <div class="editor-block">
        <div class="editor-heading">
          <label>
            <i class="fas fa-paint-brush me-2"></i>
            Custom CSS
          </label>
          <span>@CustomCss</span>
        </div>
        <textarea
          class="form-control font-monospace"
          rows="9"
          [placeholder]="cssPlaceholder"
          [(ngModel)]="customCss"
          (input)="generatedCertificateHtml = ''"
        ></textarea>
      </div>

      <div class="editor-block mb-0">
        <div class="editor-heading">
          <label>
            <i class="fas fa-align-center me-2"></i>
            Footer HTML
          </label>
          <span>@FooterHtml</span>
        </div>
        <textarea
          class="form-control font-monospace"
          rows="7"
          [placeholder]="footerPlaceholder"
          [(ngModel)]="footerHtml"
          (input)="generatedCertificateHtml = ''"
        ></textarea>
      </div>
    </div>

    <div
      *ngIf="generatedCertificateHtml"
      class="success-message"
    >
      <i class="fas fa-check-circle me-2"></i>
      Certificate generated successfully. You can now View or Download it.
    </div>

    <div class="action-bar">
      <div class="action-buttons">
        <button
          type="button"
          class="btn secondary-action"
          [disabled]="!generatedCertificateHtml || isGenerating"
          (click)="viewGeneratedCertificate()"
        >
          <i class="fas fa-eye me-2"></i>
          View
        </button>

        <button
          type="button"
          class="btn download-action"
          [disabled]="!generatedCertificateHtml || isGenerating"
          (click)="downloadGeneratedCertificate()"
        >
          <i class="fas fa-download me-2"></i>
          Download
        </button>

        <button
          type="button"
          class="btn generate-action"
          [disabled]="
            isGenerating ||
            !selectedModuleName ||
            selectedEntityId <= 0 ||
            !selectedTemplateId
          "
          (click)="generateCertificate()"
        >
          <ng-container *ngIf="!isGenerating; else generatingTemplate">
            <i class="fas fa-certificate me-2"></i>
            Generate Certificate
          </ng-container>
          <ng-template #generatingTemplate>
            <span class="spinner-border spinner-border-sm me-2"></span>
            Generating...
          </ng-template>
        </button>
      </div>
    </div>
  </div>

  <!-- ============================================================== -->
  <!-- TAB 2: AI DYNAMIC SQL TEMPLATE GENERATION -->
  <!-- ============================================================== -->
  <div *ngIf="activeTab === 'aiTemplate'">
    <!-- Top Selection Card -->
    <div class="certificate-card selection-card">
      <div class="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3 pb-2 border-bottom">
        <div>
          <h5 class="m-0 fw-bold" style="color: #011044;">
            <i class="fas fa-magic me-2" style="color: #ba025e;"></i>
            AI Dynamic SQL Template Generator
          </h5>
          <p class="m-0 text-muted" style="font-size: 13px;">
            Transform HTML certificate designs into dynamic SQL print templates for your organization
          </p>
        </div>

        <!-- Mode Toggle: New Template vs Modify Existing Template -->
        <div class="template-mode-toggle">
          <button
            type="button"
            class="toggle-btn"
            [class.active]="templateMode === 'new'"
            (click)="setTemplateMode('new')"
          >
            <i class="fas fa-plus-circle me-1"></i>
            Create New Template
          </button>
          <button
            type="button"
            class="toggle-btn"
            [class.active]="templateMode === 'modify'"
            (click)="setTemplateMode('modify')"
          >
            <i class="fas fa-edit me-1"></i>
            Modify Existing Template
          </button>
        </div>
      </div>

      <!-- Entity & Template Selection (Matching Tab 1 styling) -->
      <div class="row g-3">
        <!-- Module Selection -->
        <div class="col-lg-4 col-md-6">
          <label class="form-label">
            Select Module <span class="text-danger">*</span>
          </label>
          <select
            class="form-select"
            [(ngModel)]="selectedAiModuleName"
            (change)="onAiModuleChange()"
          >
            <option value="">Select Module</option>
            <option
              *ngFor="let mod of moduleOptions"
              [value]="mod.value"
            >
              {{ mod.label }}
            </option>
          </select>
        </div>

        <!-- Dynamic Entity Selection (Hospital / School / Society) -->
        <div class="col-lg-4 col-md-6">
          <label class="form-label">
            Select {{ aiEntityLabel }} <span class="text-danger">*</span>
          </label>
          <div
            *ngIf="isAiEntityLoading"
            class="form-control loading-control"
          >
            <span class="spinner-border spinner-border-sm me-2"></span>
            Loading {{ aiEntityLabel }} list...
          </div>
          <select
            *ngIf="!isAiEntityLoading"
            class="form-select"
            [(ngModel)]="selectedAiEntityId"
            [disabled]="!selectedAiModuleName"
            (change)="onAiEntityChange()"
          >
            <option [ngValue]="0">Select {{ aiEntityLabel }}</option>
            <option
              *ngFor="let entity of aiEntityList"
              [ngValue]="getEntityId(entity)"
            >
              {{ getEntityName(entity) }}
            </option>
          </select>
          <small
            *ngIf="selectedAiModuleName && !isAiEntityLoading && aiEntityList.length === 0"
            class="text-danger d-block mt-1"
          >
            No active/onboarded {{ aiEntityLabel }} found.
          </small>
        </div>

        <!-- Template Type Selection -->
        <div class="col-lg-4 col-md-6">
          <label class="form-label">
            Template Type <span class="text-danger">*</span>
          </label>
          <div
            *ngIf="isAiTemplateLoading"
            class="form-control loading-control"
          >
            <span class="spinner-border spinner-border-sm me-2"></span>
            Loading template types...
          </div>
          <select
            *ngIf="!isAiTemplateLoading"
            class="form-select"
            [(ngModel)]="selectedAiTemplateType"
            [disabled]="!selectedAiModuleName"
            (change)="onAiTemplateChange()"
          >
            <option value="">Select Template Type</option>
            <option
              *ngFor="let t of aiTemplateList"
              [value]="t"
            >
              {{ t }}
            </option>
          </select>
          <small
            *ngIf="selectedAiModuleName && !isAiTemplateLoading && aiTemplateList.length === 0"
            class="text-danger d-block mt-1"
          >
            No template types available for {{ selectedAiModuleName }}.
          </small>
        </div>
      </div>
    </div>

    <!-- Mode-Specific Notice Banner -->
    <div *ngIf="templateMode === 'modify' && selectedAiModuleName && selectedAiEntityId > 0 && selectedAiTemplateType" class="mb-3">
      <div *ngIf="isCheckingExistingTemplate" class="alert alert-light border d-flex align-items-center p-3 mb-0" style="border-radius: 10px;">
        <span class="spinner-border spinner-border-sm me-2 text-primary"></span>
        <span class="small">Checking existing template for {{ getSelectedAiEntityName() }}...</span>
      </div>

      <div *ngIf="!isCheckingExistingTemplate && existingTemplateFound" class="alert alert-success d-flex align-items-center p-3 mb-0" style="border-radius: 10px; background: #edf9f2; border-color: #cce9d9; color: #147344;">
        <i class="fas fa-check-circle fs-5 me-2"></i>
        <div>
          <strong class="d-block">Existing Custom Template Found</strong>
          <span class="small">
            AI will reference this existing template for <strong>{{ getSelectedAiEntityName() }}</strong> and apply your modifications in-place.
          </span>
        </div>
      </div>

      <div *ngIf="!isCheckingExistingTemplate && !existingTemplateFound" class="alert alert-info d-flex align-items-center p-3 mb-0" style="border-radius: 10px; background: #f0f7ff; border-color: #d0e7ff; color: #0c5460;">
        <i class="fas fa-info-circle fs-5 me-2"></i>
        <div>
          <strong class="d-block">No Custom Template Created Yet for This {{ aiEntityLabel }}</strong>
          <span class="small">
            AI will fetch the standard default template as reference and save the modified result as a custom template for <strong>{{ getSelectedAiEntityName() }}</strong>.
          </span>
        </div>
      </div>
    </div>

    <!-- Design & Prompt Input Card -->
    <div class="certificate-card editor-card">
      <!-- HTML File Upload Box -->
      <div class="editor-block">
        <div class="editor-heading">
          <label>
            <i class="fas fa-file-code me-2"></i>
            HTML Design File (.html or .htm)
            <span *ngIf="templateMode === 'new'" class="text-danger">*</span>
          </label>
          <span *ngIf="templateMode === 'modify'">Optional if prompt provided</span>
        </div>

        <div class="file-dropzone" [class.has-file]="uploadedFileName">
          <input
            #fileInput
            type="file"
            accept=".html,.htm"
            class="d-none"
            (change)="onHtmlFileSelected($event)"
          />

          <div *ngIf="!uploadedFileName" class="dropzone-empty" (click)="fileInput.click()">
            <div class="dropzone-icon">
              <i class="fas fa-cloud-upload-alt"></i>
            </div>
            <div class="dropzone-title">Click to upload or drag & drop .html design file</div>
            <div class="dropzone-hint">Supports standard HTML with tables, signatures, logos, and inline CSS (max 500 KB)</div>
          </div>

          <div *ngIf="uploadedFileName" class="dropzone-filled d-flex align-items-center justify-content-between p-3">
            <div class="d-flex align-items-center">
              <div class="file-badge me-3">
                <i class="fas fa-file-code"></i>
              </div>
              <div>
                <div class="fw-bold text-dark">{{ uploadedFileName }}</div>
                <div class="text-muted small">{{ uploadedFileSizeKb }} KB &bull; Ready for dynamic SQL conversion</div>
              </div>
            </div>

            <div class="btn-group">
              <button
                type="button"
                class="btn btn-sm btn-outline-secondary"
                (click)="fileInput.click()"
              >
                <i class="fas fa-sync-alt me-1"></i> Replace
              </button>
              <button
                type="button"
                class="btn btn-sm btn-outline-danger"
                (click)="clearSelectedFile(fileInput)"
              >
                <i class="fas fa-times me-1"></i> Clear
              </button>
            </div>
          </div>
        </div>

        <div *ngIf="fileUploadError" class="text-danger small mt-2">
          <i class="fas fa-exclamation-circle me-1"></i> {{ fileUploadError }}
        </div>
      </div>

      <!-- Custom Prompt / Nuance Instructions -->
      <div class="editor-block mb-0">
        <div class="editor-heading">
          <label>
            <i class="fas fa-comment-alt me-2"></i>
            Custom Prompt / Nuance Instructions
            <span *ngIf="templateMode === 'modify' && !uploadedFileName" class="text-danger">*</span>
          </label>
          <span>Optional formatting notes</span>
        </div>
        <textarea
          class="form-control font-monospace"
          rows="4"
          placeholder="e.g. Change title font to bold navy blue (#002060), add a double border around the certificate, adjust padding..."
          [(ngModel)]="aiCustomPrompt"
        ></textarea>
        <small class="text-muted d-block mt-1">
          Specify styling nuances, colors, column sizes, or specific dynamic token placement.
        </small>
      </div>
    </div>

    <!-- Action Bar -->
    <div class="action-bar mb-3">
      <div class="action-buttons">
        <button
          type="button"
          class="btn generate-action"
          [disabled]="
            isGeneratingAi ||
            !selectedAiModuleName ||
            selectedAiEntityId <= 0 ||
            !selectedAiTemplateType ||
            (templateMode === 'new' && !uploadedHtmlContent) ||
            (templateMode === 'modify' && !uploadedHtmlContent && !aiCustomPrompt?.trim())
          "
          (click)="generateAiTemplate()"
        >
          <ng-container *ngIf="!isGeneratingAi">
            <i class="fas fa-magic me-2"></i>
            {{ templateMode === 'new' ? 'Generate Dynamic SQL Template' : 'Update Dynamic SQL Template' }}
          </ng-container>
          <ng-template #generatingState>
            <span class="spinner-border spinner-border-sm me-2"></span>
            Generating Template...
          </ng-template>
        </button>
      </div>
    </div>

    <!-- ============================================================== -->
    <!-- ANIMATED LOADING STATE -->
    <!-- ============================================================== -->
    <div *ngIf="isGeneratingAi" class="certificate-card loading-card mb-3 p-4">
      <div class="neural-pulse-wrapper mb-3">
        <div class="pulse-ring ring-1"></div>
        <div class="pulse-ring ring-2"></div>
        <div class="pulse-ring ring-3"></div>
        <div class="neural-core">
          <i class="fas fa-magic text-white"></i>
        </div>
      </div>

      <div class="text-center">
        <h5 class="fw-bold mb-1" style="color: #011044;">
          Synthesizing Dynamic SQL Template
        </h5>
        <p class="text-muted small mb-3">
          Module: <strong>{{ selectedAiModuleName }}</strong> &bull; Entity: <strong>{{ getSelectedAiEntityName() }}</strong>
        </p>

        <!-- Progress Bar -->
        <div class="progress custom-progress mb-2" style="height: 8px; border-radius: 6px;">
          <div
            class="progress-bar progress-bar-striped progress-bar-animated"
            role="progressbar"
            style="background: linear-gradient(90deg, #011044, #ba025e);"
            [style.width.%]="loadingProgress"
          ></div>
        </div>
        <div class="d-flex justify-content-between text-muted small mb-3">
          <span>{{ loadingPhaseText }}</span>
          <span class="fw-bold text-primary">{{ loadingProgress }}%</span>
        </div>

        <!-- 4-Step Stepper -->
        <div class="stepper-horizontal">
          <div class="step-item" [class.completed]="loadingPhase > 1" [class.active]="loadingPhase === 1">
            <div class="step-icon">
              <i class="fas" [class.fa-check]="loadingPhase > 1" [class.fa-database]="loadingPhase <= 1"></i>
            </div>
            <div class="step-label">1. Schema Reference</div>
          </div>
          <div class="step-line" [class.active]="loadingPhase >= 2"></div>
          <div class="step-item" [class.completed]="loadingPhase > 2" [class.active]="loadingPhase === 2">
            <div class="step-icon">
              <i class="fas" [class.fa-check]="loadingPhase > 2" [class.fa-magic]="loadingPhase <= 2"></i>
            </div>
            <div class="step-label">2. AI Synthesizer</div>
          </div>
          <div class="step-line" [class.active]="loadingPhase >= 3"></div>
          <div class="step-item" [class.completed]="loadingPhase > 3" [class.active]="loadingPhase === 3">
            <div class="step-icon">
              <i class="fas" [class.fa-check]="loadingPhase > 3" [class.fa-shield-alt]="loadingPhase <= 3"></i>
            </div>
            <div class="step-label">3. SQL Validation</div>
          </div>
          <div class="step-line" [class.active]="loadingPhase >= 4"></div>
          <div class="step-item" [class.completed]="loadingProgress === 100" [class.active]="loadingPhase === 4">
            <div class="step-icon">
              <i class="fas" [class.fa-check]="loadingProgress === 100" [class.fa-save]="loadingProgress < 100"></i>
            </div>
            <div class="step-label">4. Save Template</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Error Alert -->
    <div *ngIf="aiErrorMessage && !isGeneratingAi" class="alert alert-danger mb-3 d-flex align-items-start p-3" style="border-radius: 10px;" role="alert">
      <i class="fas fa-exclamation-triangle text-danger fs-4 me-3 mt-1"></i>
      <div>
        <h6 class="fw-bold mb-1">Template Generation Failed</h6>
        <div class="small">{{ aiErrorMessage }}</div>
      </div>
    </div>

    <!-- ============================================================== -->
    <!-- SUCCESS CELEBRATION CARD WITH PREVIEW -->
    <!-- ============================================================== -->
    <div *ngIf="aiSuccessResult && !isGeneratingAi" class="certificate-card success-card mb-3 p-4">
      <!-- Success Header Banner -->
      <div class="d-flex align-items-center justify-content-between flex-wrap gap-3 pb-3 border-bottom">
        <div class="d-flex align-items-center">
          <div class="checkmark-circle me-3">
            <i class="fas fa-check"></i>
          </div>
          <div>
            <h5 class="fw-bold text-success mb-1">
              {{ aiSuccessResult.message || 'Dynamic SQL Template Generated & Saved Successfully!' }}
            </h5>
            <div class="text-muted small">
              Template has been saved and registered for {{ getSelectedAiEntityName() }}.
            </div>
          </div>
        </div>

        <span class="badge px-3 py-2 fw-semibold" style="background: #edf9f2; color: #147344; border: 1px solid #cce9d9; border-radius: 20px;">
          <i class="fas fa-check-circle me-1"></i> Ready for Production
        </span>
      </div>

      <!-- Informative Instructions Box -->
      <div class="instruction-callout mt-3 p-3 rounded-3">
        <div class="d-flex align-items-start">
          <i class="fas fa-lightbulb text-warning fs-4 me-3 mt-1"></i>
          <div>
            <div class="fw-bold text-dark mb-1">
              Next Step:
            </div>
            <p class="mb-0 text-secondary" style="font-size: 13px;">
              Once you log in with <strong>{{ getSelectedAiEntityName() }}</strong> and go to Certificate Generation to generate the <strong>{{ selectedAiTemplateType }}</strong> certificate, you will be able to see this newly saved template rendered with all live dynamic data!
            </p>
          </div>
        </div>
      </div>

      <!-- Result Metadata Tags -->
      <div class="row g-2 mt-2 mb-3">
        <div class="col-auto">
          <span class="meta-pill">
            <i class="fas fa-layer-group me-1" style="color: #ba025e;"></i>
            <strong>Module:</strong> {{ aiSuccessResult.moduleName }}
          </span>
        </div>
        <div class="col-auto">
          <span class="meta-pill">
            <i class="fas fa-building me-1" style="color: #ba025e;"></i>
            <strong>Organization:</strong> {{ getSelectedAiEntityName() }}
          </span>
        </div>
        <div class="col-auto">
          <span class="meta-pill">
            <i class="fas fa-file-alt me-1" style="color: #ba025e;"></i>
            <strong>Template Type:</strong> {{ aiSuccessResult.masterType }}
          </span>
        </div>
      </div>

      <!-- Live Generated Preview Section -->
      <div class="preview-box border rounded-3 p-3 bg-white mt-3">
        <div class="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
          <div class="fw-bold" style="color: #011044;">
            <i class="fas fa-eye me-2" style="color: #ba025e;"></i>
            Generated Template Preview (with Entity Details)
          </div>

          <div class="btn-group">
            <button
              type="button"
              class="btn btn-sm btn-outline-primary"
              (click)="viewAiPreviewInNewTab()"
            >
              <i class="fas fa-external-link-alt me-1"></i> View Fullscreen
            </button>
            <button
              type="button"
              class="btn btn-sm btn-outline-success"
              (click)="downloadAiPreviewHtml()"
            >
              <i class="fas fa-download me-1"></i> Download HTML
            </button>
            <button
              type="button"
              class="btn btn-sm btn-outline-secondary"
              (click)="copyGeneratedSql()"
            >
              <i class="fas fa-copy me-1"></i> Copy SQL
            </button>
          </div>
        </div>

        <!-- Rendered Preview Frame -->
        <div class="rendered-frame border rounded p-3 bg-light">
          <div *ngIf="isPreviewLoading" class="text-center py-4">
            <span class="spinner-border spinner-border-sm me-2" style="color: #ba025e;"></span>
            Rendering certificate preview...
          </div>

          <div *ngIf="!isPreviewLoading && previewCertificateHtml" class="rendered-content" [innerHTML]="previewCertificateHtml"></div>

          <div *ngIf="!isPreviewLoading && !previewCertificateHtml" class="text-center py-3 text-muted">
            <i class="fas fa-check-circle fs-2 text-success mb-2 d-block"></i>
            <div>Template successfully generated and registered in the database.</div>
            <div class="small">Click "View Fullscreen" to open the rendered design in a new window.</div>
          </div>
        </div>
      </div>

      <!-- Collapsible SQL Code Box -->
      <div class="sql-box border rounded-3 p-3 bg-white mt-3">
        <div class="d-flex align-items-center justify-content-between mb-2">
          <div class="fw-bold" style="color: #011044; font-size: 13px;">
            <i class="fas fa-code me-2" style="color: #ba025e;"></i>
            Generated Dynamic SQL Template (NVARCHAR Format)
          </div>
          <button
            type="button"
            class="btn btn-sm btn-outline-secondary"
            (click)="isSqlPreviewCollapsed = !isSqlPreviewCollapsed"
          >
            <i class="fas" [class.fa-chevron-down]="isSqlPreviewCollapsed" [class.fa-chevron-up]="!isSqlPreviewCollapsed"></i>
            {{ isSqlPreviewCollapsed ? 'Show SQL Code' : 'Hide SQL Code' }}
          </button>
        </div>

        <div *ngIf="!isSqlPreviewCollapsed" class="mt-2">
          <pre class="font-monospace p-3 bg-dark text-light rounded mb-0" style="max-height: 350px; overflow-y: auto; font-size: 12px;"><code>{{ aiSuccessResult.generatedSql }}</code></pre>
        </div>
      </div>
    </div>
  </div>
</div>
